/* eslint-disable ember/classic-decorator-hooks */
import EmberObject from '@ember/object';

import ActualFilterAmplitude from './actual-filter-amplitude';
import NoiseGroup from './noise-group';
import IdealOpAmp from './ideal-op-amp';
import * as CommonUtils from '../../../../utils/common';
import LowPassMultipleFeedbackCircuit from './circuit';
import * as CircuitUtils from '../../../utils/circuit';
import * as MultipleFeedbackLowPassCircuitLimitsUtils from './circuit-limits-utils';
import * as ComponentFactory from '../../../components/components-factory';
import { getImplementation } from '../../../gestures/optimization';
import FilterConstants from '../../../../filter-constants';
import ToleranceInput from '../../../inputs/tolerance';
import { computeNominalValue } from 'filter-wizard/models/tolerances/tolerance-utils';

import { calculateFilterComponents } from '@ebiz/aditools-filter';

// Encapsulates the component creation of multiplefeedback lowpass
// A circuit is created from the stage properties stageFp, stageQ, and gain, and the optimization;
export default class MultipleFeedbackLowPassCircuit extends EmberObject {
  // Inputs
  stageFp;
  stageQ;
  gain;
  optimization;
  algorithm;

  // Process output - something used internally but on other classes
  supportedComponents;
  actualFilterAmplitudeModel;
  noiseGroupModel;
  idealOpAmpModel;
  circuitModel;
  idealOpAmpValuePaths;
  componentLimits;
  frequency;
  sizing;

  // Figures output - used internally and also as output in the figures
  needReferenceStage = true;
  c1;
  c2;
  r1;
  r2;
  r5;
  masterCapacitor;
  allowGainChange;
  opAmp;

  // For cGov params of calculateFilterComponents - aditools-filter
  c2IdealValue;

  init() {
    super.init();

    if (
      CommonUtils.isNullOrUndefined(this.stageFp) ||
      CommonUtils.isNullOrUndefined(this.stageQ) ||
      CommonUtils.isNullOrUndefined(this.gain) ||
      CommonUtils.isNullOrUndefined(this.optimization) ||
      CommonUtils.isNullOrUndefined(this.algorithm)
    ) {
      throw 'stageFp, stageQ, gain, optimization, and algorithm should not be null';
    }

    this.actualFilterAmplitudeModel = ActualFilterAmplitude;
    this.noiseGroupModel = NoiseGroup;
    this.idealOpAmpModel = IdealOpAmp;
    this.circuitModel = LowPassMultipleFeedbackCircuit;
    this.supportedComponents = ['r1', 'r2', 'r5', 'c1', 'c2'];
    this.idealOpAmpValuePaths = ['r1', 'r2', 'r5', 'c1', 'c2'];

    if (!CommonUtils.isNullOrUndefined(this.initialCapacitorValue)) {
      this.c2IdealValue = this._computeC2IdealValue(this.initialCapacitorValue);
    } else {
      this.c2IdealValue = this._computeC2IdealValue();
    }
    const idealValues = this._computeIdealValues();
    const capacitorLimits = MultipleFeedbackLowPassCircuitLimitsUtils.computeCapacitorLimitMultipliers(
      idealValues.c1IdealValue,
      idealValues.c2IdealValue,
      idealValues.r1IdealValue,
      idealValues.r2IdealValue,
      idealValues.r5IdealValue,
    );
    this._computeComponents(idealValues, capacitorLimits);
  }

  _computeIdealValues() {
    const implementation = getImplementation(FilterConstants.implementationType.multipleFeedback);
    const c2IdealValue = this.c2IdealValue;
    const params = {
      type: this.algorithm,
      implementation: implementation,
      fp: this.stageFp,
      gain: this.gain,
      q: this.stageQ,
      cGov: c2IdealValue,
    };
    const filterComponents = calculateFilterComponents(
      params.type,
      params.implementation,
      params.fp,
      params.gain,
      params.q,
      params.cGov,
      params.rGainOrCCross,
    );

    return {
      c1IdealValue: filterComponents.c1,
      c2IdealValue: filterComponents.c2,
      r1IdealValue: filterComponents.r1,
      r2IdealValue: filterComponents.r2,
      r5IdealValue: filterComponents.r5,
    };
  }

  _computeC1IdealValue(initialCapacitorValue, alpha, gain) {
    const c1Value = (4 / (alpha * alpha)) * (gain + 1) * initialCapacitorValue;
    return c1Value;
  }

  _computeC2IdealValue(value) {
    const initialC2Value = value ? value : this._computeInitialCapacitorValue();

    const alpha = 1 / this.stageQ;
    const c1IdealValue = this._computeC1IdealValue(initialC2Value, alpha, this.gain);
    const c1NominalValue = this._computeC1NominalValue(c1IdealValue);
    const c1NominalToIdealRatio = c1IdealValue / c1NominalValue;
    const c2IdealValue = initialC2Value / c1NominalToIdealRatio;

    return c2IdealValue;
  }

  _computeInitialCapacitorValue() {
    const initialCapacitorValue = this.optimization.determineCapacitorValue(this.stageFp);
    const toleranceInput = new ToleranceInput({
      capacitorTolerance: FilterConstants.capacitorTolerance.ideal,
      capacitorPreferredSeries: 'E24',
    });
    const nominalValue = computeNominalValue(initialCapacitorValue, toleranceInput.capacitorPreferredSeries, 'closest');
    return nominalValue;
  }

  _computeC1NominalValue(c1IdealValue) {
    const toleranceInput = new ToleranceInput({
      capacitorTolerance: FilterConstants.capacitorTolerance.ideal,
      capacitorPreferredSeries: 'E24',
    });
    const nominalValue = computeNominalValue(c1IdealValue, toleranceInput.capacitorPreferredSeries, 'closest');
    return nominalValue;
  }

  _computeComponents(idealValues, capacitorLimits) {
    this.sizing = this._getSizing(idealValues, capacitorLimits);
    this.c1 = ComponentFactory.createC1(
      idealValues.c1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      this.rcRatio,
    );
    this.rcRatioInverse = CircuitUtils.getInverseOfMasterCapacitorRatio(this.c1);
    this.r1 = ComponentFactory.createR1(
      idealValues.r1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      this.rcRatioInverse,
    );
    this.r2 = ComponentFactory.createR2(
      idealValues.r2IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      this.rcRatioInverse,
    );
    this.c2 = ComponentFactory.createC2(
      idealValues.c2IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      this.rcRatioInverse,
    );
    this.r5 = ComponentFactory.createR2(
      idealValues.r5IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
      this.sizing,
      this.rcRatioInverse,
    );
    this.frequency = 1 / (2 * Math.PI * Math.sqrt(this.r2.value * this.r5.value * this.c1.value * this.c2.value));
    this.masterCapacitor = this.c2;
  }

  _getSizing(idealValues, capacitorLimits) {
    let r1 = ComponentFactory.createR1(
      idealValues.r1IdealValue,
      capacitorLimits.capacitorMinimumLimitMultiplier,
      capacitorLimits.capacitorMaximumLimitMultiplier,
    );
    return CircuitUtils.getResistorSize(r1);
  }

  // Multiple feedback circuits doesn't have gain resistors.
  // This is left empty intentionally.
  changeGainValues() {}

  updateR4() {
    throw 'r4 is not supported in this circuit';
  }

  changeInitialCapacitor(value) {
    this.c2IdealValue = this._computeC2IdealValue(value);
    const idealValues = this._computeIdealValues();
    const capacitorLimits = MultipleFeedbackLowPassCircuitLimitsUtils.computeCapacitorLimitMultipliers(
      idealValues.c1IdealValue,
      idealValues.c2IdealValue,
      idealValues.r1IdealValue,
      idealValues.r2IdealValue,
      idealValues.r5IdealValue,
    );
    this._computeComponents(idealValues, capacitorLimits);
  }
}
